import { MapPin, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useVersion } from '../contexts/VersionContext'
import { LoveStoryTimeline } from '../components/LoveStoryTimeline'

export function Home() {
  const { isV1 } = useVersion()
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-7xl font-serif text-primary-900 mb-4 text-center">
            Naetzy & Eduardo
          </h1>
          <div className="flex flex-row items-center justify-center gap-4 text-lg text-primary-800 animate-slide-up">
            <span>April 4, 2026</span>
            <span className="text-primary-600">|</span>
            <span>Oaxaca, Mexico</span>
          </div>
          
          <div className="mt-8 animate-fade-in lg:hidden">
            <Link
              to="/rsvp"
              className="inline-block px-6 py-3 bg-primary-600 text-white font-medium rounded-md border-2 border-primary-600 hover:bg-primary-700 hover:border-primary-700 shadow-md hover:shadow-lg transition-all duration-200 text-lg"
            >
              RSVP
            </Link>
          </div>
        </div>
      </section>

      {/* Wedding Details */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-primary-900 mb-4">
              Join Us for Our Special Day
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              We are so honored to have our friends and family join us in Oaxaca, Mexico to celebrate our big day! 
              Please find important details about our wedding, including the RSVP, travel information, 
              guest accommodations, and links to our registry.
            </p>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto mt-4">
              Love,<br />
              Naetzy & Eduardo
            </p>
          </div>

          {!isV1 && (
            <div className="space-y-12">
              {/* Friday, April 3rd Section */}
              <div>
                <h3 className="text-2xl font-serif text-primary-900 mb-3 text-center">
                  Friday, April 3rd
                </h3>
                <p className="text-center text-sm text-neutral-500 mb-8 max-w-md mx-auto">
                  Calenda and Welcome Drinks have updated times; earlier times are shown crossed out.
                </p>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Calenda */}
                  <div className="card p-8 text-center bg-primary-50/35 shadow-sm ring-1 ring-inset ring-primary-200/70">
                    <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-8 w-8 text-secondary-600" />
                    </div>
                    <div className="flex flex-col items-center gap-2 mb-2">
                      <h3 className="text-xl font-serif text-primary-900">Calenda</h3>
                      <span className="inline-flex items-center rounded-full border border-primary-200/90 bg-white/80 px-2.5 py-1 text-xs font-medium text-primary-800">
                        Schedule updated
                      </span>
                    </div>
                    <p className="text-neutral-600 mb-2">
                      <span className="line-through text-neutral-400 mr-2" title="Previous time">
                        6:00 PM
                      </span>
                      <span className="font-medium text-primary-900">4:00 PM</span>
                    </p>
                  </div>

                  {/* Welcome Drinks */}
                  <div className="card p-8 text-center bg-primary-50/35 shadow-sm ring-1 ring-inset ring-primary-200/70">
                    <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-8 w-8 text-primary-600" />
                    </div>
                    <div className="flex flex-col items-center gap-2 mb-2">
                      <h3 className="text-xl font-serif text-primary-900">Welcome Drinks</h3>
                      <span className="inline-flex items-center rounded-full border border-primary-200/90 bg-white/80 px-2.5 py-1 text-xs font-medium text-primary-800">
                        Schedule updated
                      </span>
                    </div>
                    <p className="text-neutral-600 mb-2">
                      <span className="line-through text-neutral-400 mr-2" title="Previous time">
                        7:00 PM
                      </span>
                      <span className="font-medium text-primary-900">5:30 PM - 8:30 PM</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Saturday, April 4th Section */}
              <div>
                <h3 className="text-2xl font-serif text-primary-900 mb-6 text-center">
                  Saturday, April 4th
                </h3>
                <div className="grid md:grid-cols-3 gap-8">
                  {/* Ceremony */}
                  <div className="card p-8 text-center">
                    <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-8 w-8 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-serif text-primary-900 mb-2">Ceremony</h3>
                    <p className="text-neutral-600 mb-4">5:00 PM - 6:00 PM</p>
                  </div>

                  {/* Cocktail Hour */}
                  <div className="card p-8 text-center">
                    <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-8 w-8 text-secondary-600" />
                    </div>
                    <h3 className="text-xl font-serif text-primary-900 mb-2">Cocktail Hour</h3>
                    <p className="text-neutral-600 mb-4">6:00 PM - 7:00 PM</p>
                  </div>

                  {/* Reception */}
                  <div className="card p-8 text-center">
                    <div className="h-16 w-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-8 w-8 text-accent-600" />
                    </div>
                    <h3 className="text-xl font-serif text-primary-900 mb-2">Reception</h3>
                    <p className="text-neutral-600 mb-4">7:00 PM - Late</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Venue Information */}
      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-serif text-primary-900 mb-6 text-center">
                Venue Location
              </h2>
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-primary-900 mb-2">
                  <a 
                    href="https://www.casadonluis.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary-700 transition-colors duration-200 underline decoration-primary-300 hover:decoration-primary-600"
                  >
                    Casa Don Luis Jardín Boutique
                  </a>
                </h3>
                <p className="text-neutral-600 mb-1">
                  <a 
                    href="https://maps.app.goo.gl/CyURXiHX3YdqK82R6" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary-700 transition-colors duration-200 underline decoration-neutral-300 hover:decoration-primary-600"
                  >
                    Camino Real #1201, San Agustín de las Juntas, Oaxaca, Mexico
                  </a>
                </p>
                <p className="text-neutral-600 mb-1">
                    
                </p>
              </div>
              {!isV1 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary-900 mb-1">Transportation</h4>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        Transportation will be provided to and from some of the hotels listed in the Accommodations & Travel page. Stay tuned for more details!
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary-900 mb-1">Arrival Time</h4>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        The ceremony starts promptly at 5:00 PM. Please arrive by 4:45 PM.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <img
                src="https://static.wixstatic.com/media/9c3ef0_2eccb3a3c3f246bf98f210ccec5762c0~mv2_d_5503_3669_s_4_2.jpg/v1/fit/w_2900,h_980,q_90,enc_avif,quality_auto/9c3ef0_2eccb3a3c3f246bf98f210ccec5762c0~mv2_d_5503_3669_s_4_2.jpg"
                alt="Casa Don Luis Jardín Boutique - Wedding venue"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Love Story Timeline */}
      <LoveStoryTimeline />
    </div>
  )
} 